import { NgClass } from '@angular/common'
import {
    Component,
    inject,
    signal,
    computed,
    input,
    OnInit,
    OnDestroy,
    HostListener,
    ElementRef,
} from '@angular/core'
import { RouterModule } from '@angular/router'
import { AuthStateService } from '@mattrika/benefit-core'
import { NgIcon, provideIcons } from '@ng-icons/core'
import { lucideChevronDown, lucideMenu, lucideX } from '@ng-icons/lucide'
import { HlmButtonImports } from '@spartan-ng/helm/button'
import { HeaderProfileMenuComponent } from '../header-profile-menu/header-profile-menu.component'
import { HeaderClientMegaMenuComponent } from './components/header-client-mega-menu/header-client-mega-menu.component'
import { HeaderClientMobileDrawerComponent } from './components/header-client-mobile-drawer/header-client-mobile-drawer.component'
import {
    DEFAULT_DEAL_GROUPS,
    DEFAULT_FUN_ACTIVITIES,
} from './header-client.data'
import { HeaderMenuType, DealGroup, FunActivity } from './header-client.types'

export * from './header-client.types'
export * from './header-client.data'

@Component({
    selector: 'app-header-client',
    imports: [
        NgIcon,
        RouterModule,
        NgClass,
        ...HlmButtonImports,
        HeaderProfileMenuComponent,
        HeaderClientMegaMenuComponent,
        HeaderClientMobileDrawerComponent,
    ],
    providers: [
        provideIcons({
            lucideChevronDown,
            lucideMenu,
            lucideX,
        }),
    ],
    templateUrl: './header-client.component.html',
    styleUrl: './header-client.component.css',
})
export class HeaderClientComponent implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef)
    readonly authState = inject(AuthStateService)

    // Close debounce timer & scroll listeners
    private closeTimeout: ReturnType<typeof setTimeout> | null = null
    private scrollListener: (() => void) | null = null

    // Organized Deals & Fun Configurations
    readonly dealGroups: DealGroup[] = DEFAULT_DEAL_GROUPS
    readonly funActivities: FunActivity[] = DEFAULT_FUN_ACTIVITIES

    pageTitle = input<string>('')
    userName = input<string>('')
    userRole = input<string>('')
    userEmail = input<string>('')

    // Active mega menu ('deals' | 'fun' | null)
    activeMenu = signal<HeaderMenuType | null>(null)

    // Mobile navigation state
    isMobileMenuOpen = signal<boolean>(false)

    // Header scroll state
    isScrolled = signal<boolean>(false)

    readonly displayName = computed(
        () => this.userName() || this.authState.userNameSignal() || 'Client',
    )
    readonly displayRole = computed(
        () => this.userRole() || this.authState.userRoleSignal() || 'Client',
    )
    readonly displayEmail = computed(
        () => this.userEmail() || this.authState.userEmailSignal() || '',
    )
    readonly displayInitials = computed(() => {
        const name = this.userName()?.trim()
        if (name) {
            const parts = name.split(/\s+/)
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase()
            }
            if (name.length >= 2) {
                return name.slice(0, 2).toUpperCase()
            }
        }
        return this.authState.userInitialsSignal()
    })
    readonly isLoggedIn = computed(() => this.authState.isLoggedInSignal())
    readonly showClientDashboard = computed(
        () =>
            this.isLoggedIn() &&
            this.displayRole() === 'Client' &&
            !this.authState.isOwnerSignal(),
    )

    ngOnInit() {
        this.scrollListener = () => {
            this.isScrolled.set(window.scrollY > 15)
        }
        window.addEventListener('scroll', this.scrollListener, {
            passive: true,
        })
    }

    ngOnDestroy() {
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener)
        }
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
        }
        if (typeof document !== 'undefined') {
            document.body.style.overflow = ''
        }
    }

    @HostListener('document:keydown.escape')
    onEscapePress() {
        this.closeAllMenus()
        this.closeMobileMenu()
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeAllMenus()
            this.closeMobileMenu()
        }
    }

    // Hover & Menu Interactions with zero-gap safety
    openMenu(menu: HeaderMenuType) {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
        }
        this.activeMenu.set(menu)
    }

    scheduleClose() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
        }
        this.closeTimeout = setTimeout(() => {
            this.activeMenu.set(null)
        }, 350)
    }

    cancelClose() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
        }
    }

    toggleNavMenu(menu: HeaderMenuType, event?: Event) {
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }
        if (this.activeMenu() === menu) {
            this.closeAllMenus()
        } else {
            this.openMenu(menu)
        }
    }

    closeAllMenus() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
        }
        this.activeMenu.set(null)
    }

    // Mobile Navigation
    toggleMobileMenu() {
        this.isMobileMenuOpen.update((v) => !v)
        if (this.isMobileMenuOpen()) {
            this.closeAllMenus()
            if (typeof document !== 'undefined') {
                document.body.style.overflow = 'hidden'
            }
        } else {
            if (typeof document !== 'undefined') {
                document.body.style.overflow = ''
            }
        }
    }

    closeMobileMenu() {
        this.isMobileMenuOpen.set(false)
        if (typeof document !== 'undefined') {
            document.body.style.overflow = ''
        }
    }

    logout() {
        this.closeMobileMenu()
        this.authState.logout('/login')
    }

    get initials(): string {
        return this.displayInitials()
    }
}
